import {
  IDisplayItem,
  IDisplayItemProperty,
  IProperty,
  IQuickPropertyGroupRef,
  PropertyToolType
} from "@kuika/kuika-cl-model";
import React, { useState } from "react";
import type { IScreenDesignContextState } from "../../../../../contexts/screen-design-context/screen-design-context";
import { isDataEditorProperty } from "../../../../../shared/utility/designer-property-actions";
import { PropertyHelper } from "../../../../../shared/utility/property-helper";
import { PropertySdHelper } from "../../../../../shared/utility/property-sd-helper";
import {
  IQuickOption,
  QuickBindButton,
  QuickChip,
  QuickInput,
  QuickOptionList,
  QuickPopover,
  QuickStepper,
  QuickSwatch,
  QuickSwitchChip,
  QuickTip,
  QuickToggle,
  useQuickOpenState
} from "./quick-controls";
import { QuickColumnEditor, QuickEditorBody, QuickPropertyEditor } from "./quick-editor-popover";
import { toDisplayLabel } from "./quick-labels";
import {
  choiceValueLabel,
  classifyProperty,
  formatSummaryValue,
  getEnumOptions,
  getSelectOptions,
  IChoiceOption,
  IPropertySummary,
  isBoundSymbol,
  isEmptyValue,
  isHiddenProperty,
  orderByDevice,
  parseColumnSpan,
  QuickControlKind,
  summarizeProperty,
  summarizeSymbol,
  toBoolean,
  toNumber
} from "./quick-prop-model";
import { IQuickContext } from "./quick-shared";
import { writeQuickPropertyValue } from "./quick-writes";

/** One property of the selected component, resolved the way the property panel resolves it. */
export interface IResolvedQuickProperty {
  property: IProperty;
  control: QuickControlKind;
  /** `PropertyHelper.calculatePropertyToolType` — the panel's own editor selector. */
  toolType: PropertyToolType;
  label: string;
}

const propertyHelper: PropertyHelper = new PropertyHelper();

/**
 * The overlay's sd slice is the live screen-design context (the overlay is a `withSD` consumer); the
 * platform's property helpers are typed against the whole context, of which they read only the tree,
 * the project and `getPropertyDefinition`.
 */
const asContext = (ctx: IQuickContext): IScreenDesignContextState => ctx.sd as unknown as IScreenDesignContextState;

/**
 * Resolves the named properties of the selected component. A name the component does not have, or a
 * technical / deprecated / event-typed property, is left out — the curated list is data and may be stale.
 */
export function resolveQuickProperties(ctx: IQuickContext, names: string[]): Map<string, IResolvedQuickProperty> {
  const out: Map<string, IResolvedQuickProperty> = new Map<string, IResolvedQuickProperty>();
  const context: IScreenDesignContextState = asContext(ctx);
  const properties: IProperty[] = new PropertySdHelper(
    (): IScreenDesignContextState => context
  ).getPropertiesByDisplayItem(ctx.displayItem);
  names.forEach((name: string): void => {
    const property: IProperty | undefined = properties.find((p: IProperty): boolean => p.name === name);
    if (!property || isHiddenProperty(property)) {
      return;
    }
    const toolType: PropertyToolType = propertyHelper.calculatePropertyToolType(ctx.displayItem, property);
    const isDataEditor: boolean = isDataEditorProperty(context, ctx.displayItem, name) !== null;
    out.set(name, {
      property,
      control: classifyProperty(property, toolType, isDataEditor),
      toolType,
      label: toDisplayLabel(name)
    });
  });
  return out;
}

const siblingValue =
  (di: IDisplayItem) =>
  (name: string): unknown =>
    di.item.properties?.find((p: IDisplayItemProperty): boolean => p.name === name)?.symbol?.fixedValue;

const choiceOptions = (resolved: IResolvedQuickProperty, di: IDisplayItem): IChoiceOption[] =>
  resolved.toolType === "Enum"
    ? getEnumOptions(resolved.property)
    : getSelectOptions(resolved.property, siblingValue(di));

export interface IQuickPropertyControlProps {
  ctx: IQuickContext;
  resolved: IResolvedQuickProperty;
  warnWhenEmpty?: boolean;
  /** Rendered inside a group / "more" popover: no own tooltip, the row names it. */
  isInRow?: boolean;
}

/** An editor chip's summary; a Col span reads as its number. */
function summarizeControl(ctx: IQuickContext, resolved: IResolvedQuickProperty): IPropertySummary | null {
  if (resolved.control === "column") {
    const span: number | null = parseColumnSpan(resolved.property.symbol?.fixedValue);
    return span === null ? null : { text: span.toString(), isSample: false };
  }
  return summarizeProperty(resolved.property, {
    actionDefinitions: ctx.sd.actionDefinitions,
    definedActions: ctx.sd.getDefinedActions?.()
  });
}

interface IQuickEditorPopoverProps {
  ctx: IQuickContext;
  resolved: IResolvedQuickProperty;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  children: React.ReactElement;
}

/** The property panel's own editor for one property (`PropertyFormItem`), in a sticky themed popover. */
const QuickEditorPopover = ({ ctx, resolved, isOpen, setIsOpen, children }: IQuickEditorPopoverProps): JSX.Element => (
  <QuickPopover
    open={isOpen}
    onOpenChange={setIsOpen}
    testId={`quickPropPopover-${resolved.property.name}`}
    variant="editor"
    isSticky
    content={
      <QuickEditorBody title={resolved.label} onClose={(): void => setIsOpen(false)}>
        {resolved.control === "column" ? (
          <QuickColumnEditor />
        ) : (
          <QuickPropertyEditor ctx={ctx} property={resolved.property} />
        )}
      </QuickEditorBody>
    }
  >
    {children}
  </QuickPopover>
);

/** One property → one control (see `classifyProperty`). */
export const QuickPropertyControl = ({
  ctx,
  resolved,
  warnWhenEmpty,
  isInRow
}: IQuickPropertyControlProps): JSX.Element => {
  const [isOpen, setIsOpen] = useQuickOpenState();
  // While the pointer is on the inline field's "Bind to data" button, that button's tooltip speaks.
  const [isBindHovered, setIsBindHovered] = useState<boolean>(false);
  const { property, control, label } = resolved;
  const name: string = property.name;
  const testId: string = `quickProp-${name}`;
  const popoverTestId: string = `quickPropPopover-${name}`;
  const value: unknown = property.symbol?.fixedValue;
  const isEmpty: boolean = !isBoundSymbol(property.symbol) && isEmptyValue(value);
  const tip = (element: React.ReactElement, tipLabel: string = label): React.ReactElement =>
    isInRow ? (
      element
    ) : (
      <QuickTip label={tipLabel} isSuppressed={isOpen || isBindHovered}>
        {element}
      </QuickTip>
    );
  const write = (next: unknown): void => writeQuickPropertyValue(ctx.kuikaId, name, next);

  switch (control) {
    case "text": {
      // A value prop (Label content, Button label) can be bound; its editor — binding included — is
      // one "Bind to data" click away from the typed value.
      const bindButton: JSX.Element | undefined =
        property.isValueProp === true ? (
          <QuickEditorPopover ctx={ctx} resolved={resolved} isOpen={isOpen} setIsOpen={setIsOpen}>
            <QuickTip label="Bind to data" isSuppressed={isOpen}>
              <QuickBindButton
                ariaLabel="Bind to data"
                testId={`${testId}-bind`}
                isOpen={isOpen}
                onClick={(): void => setIsOpen(!isOpen)}
                onHoverChange={setIsBindHovered}
              />
            </QuickTip>
          </QuickEditorPopover>
        ) : undefined;
      const text: string = isEmptyValue(value) ? "" : String(value);
      // The field truncates long text; the tooltip carries the whole value.
      return tip(
        <span>
          <QuickInput
            value={text}
            placeholder="Not set"
            keyLabel={isInRow ? undefined : label}
            ariaLabel={label}
            testId={testId}
            trailing={bindButton}
            onCommit={write}
          />
        </span>,
        text === "" ? label : `${label}: ${text}`
      );
    }
    case "number":
      return tip(
        <span>
          <QuickStepper
            value={toNumber(value)}
            ariaLabel={label}
            testId={testId}
            keyLabel={isInRow ? undefined : label}
            onCommit={write}
          />
        </span>
      );
    case "boolean": {
      const isOn: boolean = toBoolean(value);
      return tip(
        <QuickSwitchChip
          isOn={isOn}
          label={label}
          testId={testId}
          isLabelHidden={isInRow}
          onToggle={(): void => write(!isOn)}
        />
      );
    }
    case "choice": {
      const options: IChoiceOption[] = choiceOptions(resolved, ctx.displayItem);
      const current: IChoiceOption | undefined = options.find((o: IChoiceOption): boolean => o.value === String(value));
      return (
        <QuickPopover
          open={isOpen}
          onOpenChange={setIsOpen}
          testId={popoverTestId}
          content={
            <QuickOptionList
              title={label}
              options={options.map((o: IChoiceOption): IQuickOption => ({ value: o.value, label: o.label }))}
              current={current?.value}
              optionTestId={(o: IQuickOption): string => `quickPropOption-${name}-${o.value}`}
              onPick={(next: string): void => {
                setIsOpen(false);
                write(next);
              }}
            />
          }
        >
          {tip(
            <QuickChip
              testId={testId}
              ariaLabel={label}
              keyLabel={isInRow ? undefined : label}
              value={current?.label ?? "Not set"}
              isEmpty={!current}
              isWarning={warnWhenEmpty === true && !current}
              hasPopup
              isOpen={isOpen}
              onClick={(): void => setIsOpen(!isOpen)}
            />,
            current ? `${label}: ${current.label}` : label
          )}
        </QuickPopover>
      );
    }
    case "color":
    case "column":
    case "editor":
    default: {
      const summary: IPropertySummary | null = summarizeControl(ctx, resolved);
      // Sample rows are the component's placeholder, not a source: muted, and "not set" for the warn dot.
      const isPlaceholder: boolean = summary === null || summary.isSample;
      const isBound: boolean = isBoundSymbol(property.symbol);
      // Bound: the chip reads as the data it shows (glyph + field/source name); the tooltip says so.
      let tipText: string = label;
      if (isBound && summary !== null) {
        tipText = `Bound to ${summary.text} — click to change`;
      } else if (summary !== null) {
        tipText = `${label}: ${summary.fullText ?? summary.text}`;
      }
      const trigger: React.ReactElement =
        control === "color" && !isBound ? (
          <QuickToggle isOn={isOpen} label={label} testId={testId} onToggle={(): void => setIsOpen(!isOpen)}>
            <QuickSwatch color={typeof value === "string" ? value : null} />
          </QuickToggle>
        ) : (
          <QuickChip
            testId={testId}
            ariaLabel={isBound && summary !== null ? `${label}, bound to ${summary.text}` : label}
            keyLabel={isInRow ? undefined : label}
            isBound={isBound}
            value={summary?.text ?? "Not set"}
            isEmpty={isPlaceholder}
            isWarning={warnWhenEmpty === true && (isEmpty || summary?.isSample === true)}
            hasPopup
            isOpen={isOpen}
            onClick={(): void => setIsOpen(!isOpen)}
          />
        );
      return (
        <QuickEditorPopover ctx={ctx} resolved={resolved} isOpen={isOpen} setIsOpen={setIsOpen}>
          {tip(trigger, tipText)}
        </QuickEditorPopover>
      );
    }
  }
};

// ── propertyGroup ──────────────────────────────────────────────────────────────────────────────

export interface IQuickPropertyGroupProps {
  ctx: IQuickContext;
  entry: IQuickPropertyGroupRef;
  members: IResolvedQuickProperty[];
}

const memberValueText = (member: IResolvedQuickProperty): string | null => {
  const value: unknown = member.property.symbol?.fixedValue;
  if (member.control === "column") {
    return parseColumnSpan(value)?.toString() ?? null;
  }
  const symbol: IProperty["symbol"] = member.property.symbol;
  if (symbol && isBoundSymbol(symbol)) {
    return summarizeSymbol(symbol, { actionDefinitions: undefined, definedActions: undefined });
  }
  // A picked option reads as its list label ("Bottom right"), not the stored value.
  if (member.control === "choice" && !isEmptyValue(value)) {
    return choiceValueLabel(member.property, member.toolType, String(value));
  }
  return formatSummaryValue(member.control, value);
};

/**
 * The group chip's value: the first two members that have one, current device's breakpoints first.
 * Col widths keep their breakpoint ("lg 8 · md 12" — the numbers alone would not say which is which);
 * other members are just their values under the group's own key ("Pagination  On · 10").
 */
export function summarizeGroup(
  members: IResolvedQuickProperty[],
  device: IQuickContext["sd"]["selectedDeviceType"]
): string {
  const byName: Map<string, IResolvedQuickProperty> = new Map<string, IResolvedQuickProperty>(
    members.map((m: IResolvedQuickProperty): [string, IResolvedQuickProperty] => [m.property.name, m])
  );
  const ordered: string[] = orderByDevice(
    members.map((m: IResolvedQuickProperty): string => m.property.name),
    device
  );
  const parts: string[] = [];
  ordered.forEach((name: string): void => {
    const member: IResolvedQuickProperty | undefined = byName.get(name);
    const text: string | null = member ? memberValueText(member) : null;
    if (member && text !== null && parts.length < 2) {
      parts.push(member.control === "column" ? `${name} ${text}` : text);
    }
  });
  return parts.join(" · ");
}

/** A labelled cluster of properties: one chip with a summary, each member's control in the popover. */
export const QuickPropertyGroup = ({ ctx, entry, members }: IQuickPropertyGroupProps): JSX.Element => {
  const [isOpen, setIsOpen] = useQuickOpenState();
  const testId: string = `quickProp-${entry.label}`;
  const label: string = toDisplayLabel(entry.label);
  const summary: string = summarizeGroup(members, ctx.sd.selectedDeviceType);
  // Col widths are only ever edited row-wide (the panel's responsive toolkit); a group of them is that editor.
  const isColumnGroup: boolean = members.some((m: IResolvedQuickProperty): boolean => m.control === "column");
  const chip: JSX.Element = (
    <QuickChip
      testId={testId}
      ariaLabel={label}
      keyLabel={label}
      value={summary || "Not set"}
      isEmpty={!summary}
      hasPopup
      isOpen={isOpen}
      onClick={(): void => setIsOpen(!isOpen)}
    />
  );
  return (
    <QuickPopover
      open={isOpen}
      onOpenChange={setIsOpen}
      testId={`quickPropPopover-${entry.label}`}
      variant={isColumnGroup ? "editor" : "list"}
      isSticky={isColumnGroup}
      content={
        isColumnGroup ? (
          <QuickEditorBody title={label} onClose={(): void => setIsOpen(false)}>
            <QuickColumnEditor />
          </QuickEditorBody>
        ) : (
          <>
            <div className="kuika-quick-pop__title">{label}</div>
            {members.map((member: IResolvedQuickProperty): JSX.Element => (
              <div key={member.property.name} className="kuika-quick-pop__row">
                <span className="kuika-quick-pop__row-label">{member.label}</span>
                <QuickPropertyControl ctx={ctx} resolved={member} isInRow />
              </div>
            ))}
          </>
        )
      }
    >
      <QuickTip label={summary ? `${label}: ${summary}` : label} isSuppressed={isOpen}>
        {chip}
      </QuickTip>
    </QuickPopover>
  );
};
